import { Fragment } from 'react';
import { useTranslations } from '@/i18n/use-locale';
import { KeyRound, ShieldCheck, TriangleAlert } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { AdminLoginState } from '@/lib/admin-login-state';
import { cn } from '@/lib/utils';

/**
 * The two-step sign-in form (ADR-007).
 *
 * It renders state and reports intent; `useLogin` owns the flow. The challenge
 * token linking step one to step two never reaches this component and is never
 * put in a field — it lives in the hook for the length of the sign-in, which is
 * the same property the server action had and the reason neither of them keeps
 * it in the DOM.
 *
 * The code field is `dir="ltr"` inside an RTL page, per ADR-013's standing rule for OTP
 * and phone inputs: six digits typed right-to-left land in the wrong order and the admin
 * has no way to see why.
 */
export interface AdminLoginFormProps {
  readonly state: AdminLoginState;
  readonly pending: boolean;
  readonly onPassword: (email: string, password: string) => void;
  readonly onCode: (code: string) => void;
}

/**
 * A text field's value, or empty. `FormData.get` can also answer with a `File`, which a
 * login form never holds — but `String(file)` would be `[object File]`, not nothing.
 */
function textField(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

export function AdminLoginForm({ state, pending, onPassword, onCode }: AdminLoginFormProps) {
  const t = useTranslations('admin.login');

  const enrolling = state.step === 'enrol';
  const onSecondStep = state.step === 'totp' || state.step === 'enrol';

  // Still a `<form>`, and still submitted by the browser's own semantics: Enter
  // in a field submits, `required` and `minLength` are checked before anything
  // is sent, and a password manager sees a real login form. That was free under
  // a server action and is worth two lines to keep.
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    if (onSecondStep) {
      onCode(textField(data, 'code'));
      return;
    }

    onPassword(textField(data, 'email'), textField(data, 'password'));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-[17px] font-bold text-navy">
          {onSecondStep ? (
            <ShieldCheck aria-hidden className="h-[18px] w-[18px] text-pitch" strokeWidth={2} />
          ) : (
            <KeyRound aria-hidden className="h-[18px] w-[18px] text-pitch" strokeWidth={2} />
          )}
          {enrolling ? t('enrolTitle') : onSecondStep ? t('totpTitle') : t('title')}
        </h1>
        <p className="text-[12.5px] leading-relaxed text-conc-500">
          {enrolling ? t('enrolHint') : onSecondStep ? t('totpHint') : t('hint')}
        </p>
      </header>

      {state.error ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5 text-[12.5px] font-semibold text-rose-700"
        >
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
          <span>
            {state.step === 'password' && state.retryAfterSeconds
              ? t('errors.account_locked_for', {
                  minutes: Math.ceil(state.retryAfterSeconds / 60),
                })
              : t(`errors.${state.error}`)}
          </span>
        </p>
      ) : null}

      {/*
        Keyed per step, so React unmounts one step's fields before it mounts the
        next. Without the keys the two branches line up — a `Field` holding an
        uncontrolled `Input` in the same position of each — and React keeps the
        DOM node while swapping its label and `type`: the code box came up
        holding the password, in clear text. Remounting is what the browser
        would do with a second form, and it is what the uncontrolled fields
        assume.
      */}
      {onSecondStep ? (
        <Fragment key="code-step">
          {enrolling && state.totpSecret ? (
            <EnrolmentKey secret={state.totpSecret} uri={state.totpUri} />
          ) : null}

          <Field label={t('codeLabel')} htmlFor="admin-totp">
            <Input
              id="admin-totp"
              name="code"
              // `one-time-code` is what lets iOS and Android offer the code from the
              // notification, and what stops a password manager filling the field.
              autoComplete="one-time-code"
              inputMode="numeric"
              dir="ltr"
              required
              autoFocus
              maxLength={13}
              placeholder={t('codePlaceholder')}
              className="num text-center text-[18px] font-bold tracking-[0.4em]"
            />
          </Field>

          <SubmitButton label={t('verify')} pendingLabel={t('verifying')} pending={pending} />
        </Fragment>
      ) : (
        <Fragment key="password-step">
          <Field label={t('emailLabel')} htmlFor="admin-email">
            <Input
              id="admin-email"
              name="email"
              type="email"
              dir="ltr"
              autoComplete="username"
              required
              autoFocus
              placeholder={t('emailPlaceholder')}
            />
          </Field>

          <Field label={t('passwordLabel')} htmlFor="admin-password">
            <Input
              id="admin-password"
              name="password"
              type="password"
              dir="ltr"
              autoComplete="current-password"
              required
              minLength={12}
              placeholder={t('passwordPlaceholder')}
            />
          </Field>

          <SubmitButton label={t('submit')} pendingLabel={t('submitting')} pending={pending} />
        </Fragment>
      )}
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[12.5px] font-bold text-conc-600">
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * The enrolment key, shown once.
 *
 * The `otpauth://` URI the API returns is rendered as a QR the authenticator scans —
 * the path almost everyone takes from a phone. The raw base32 key stays underneath as
 * the fallback: every app supports manual entry, and it is the only way in on a desktop
 * authenticator or when the camera cannot reach the screen. The QR is plain black on
 * white with a quiet zone, because a scanner wants contrast, not brand colour.
 */
function EnrolmentKey({ secret, uri }: { secret: string; uri?: string }) {
  const t = useTranslations('admin.login');

  return (
    <div className="space-y-3 rounded-xl border border-gold-100 bg-gold-50 p-3.5">
      {uri ? (
        <div className="space-y-2">
          <p className="text-[12px] font-bold text-gold-700">{t('enrolScanLabel')}</p>
          <div className="mx-auto w-fit rounded-lg bg-white p-3">
            <QRCodeSVG
              value={uri}
              size={184}
              level="M"
              marginSize={4}
              title={t('enrolScanLabel')}
            />
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-[12px] font-bold text-gold-700">{t('enrolSecretLabel')}</p>
        <p
          dir="ltr"
          className="num rounded-lg bg-white px-3 py-2 text-center text-[14px] font-bold tracking-wider break-all text-navy select-all"
        >
          {secret}
        </p>
        <p className="text-[11.5px] leading-relaxed text-gold-700">{t('enrolNote')}</p>
      </div>
    </div>
  );
}

function SubmitButton({
  label,
  pendingLabel,
  pending,
}: {
  label: string;
  pendingLabel: string;
  pending: boolean;
}) {
  return (
    <Button type="submit" disabled={pending} className={cn('w-full')}>
      {pending ? pendingLabel : label}
    </Button>
  );
}
